import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './CollectionSelectDrawer.styles';
import { BottomSheetSelectOption } from './BottomSheetSelectOption';
import { Drawer } from '@/components/Drawer/Drawer';
import { AngleBackIcon, SearchIcon } from '@/components/UI/Icons/Icons';
import { Property, PropertyOption } from '@/api/types/categories.types';
import { Collection } from '@/components/Filters/FiltersDrawer.types';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import {
  SearchInput,
  SearchInputRef,
} from '@/components/SearchInput/SearchInput';
import { NotFound } from '@/components/NotFoundPlaceholder/NotFoundPlaceholder.styles';

type Props = {
  isInPortal?: boolean;
  isOpen: boolean;
  toggle: (v?: boolean) => void;
  propertyLegacyId?: string;
  options: PropertyOption[];
  collection?: Collection;
  allProperties?: Property[];
  popularOptions?: PropertyOption[];
  onOptionSelect?: (option: PropertyOption) => void;
};

export const CollectionSelectDrawer: FC<Props> = ({
  isOpen,
  isInPortal,
  toggle,
  propertyLegacyId,
  options,
  collection,
  allProperties,
  popularOptions,
  onOptionSelect,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<SearchInputRef>(null);

  const name = allProperties?.find(
    el => el.legacyResourceId === propertyLegacyId
  )?.name;
  const list = options?.filter(option =>
    option.name.toLowerCase().includes(query.toLowerCase())
  );
  const withPopularOptions = !!popularOptions?.length && !query;

  function handleOptionSelect(value: PropertyOption) {
    if (onOptionSelect) {
      onOptionSelect(value);
    }
  }

  function renderOption(option: PropertyOption) {
    return (
      <BottomSheetSelectOption
        key={option.name}
        dataTestId={`property-option-${option.legacyResourceId}`}
        label={option.name}
        isSelected={
          collection?.[option.propertyLegacyId] === option.legacyResourceId
        }
        onSelect={() => handleOptionSelect(option)}
      />
    );
  }

  useEffect(() => {
    searchInputRef.current?.clear();
  }, [isOpen]);

  return (
    <Drawer
      isInPortal={isInPortal}
      isOpen={isOpen}
      title={name}
      left={{
        icon: <AngleBackIcon />,
        onClick: () => toggle(false),
      }}
      dataTestId={SearchResultsPageTestIds.collectionSelectDrawer}
      hasBottomBorder={false}
      hasBottomMargin={false}
      background={theme.palette.primary.background}
      subtitle={
        <S.Container>
          <SearchInput
            ref={searchInputRef}
            onSearch={setQuery}
            leftIcon={<SearchIcon fill={theme.palette.secondary.labels} />}
            hasPadding={false}
            hasBorderBottom={false}
            customPaddingTop={12}
            customPaddingBottom={12}
          />
        </S.Container>
      }
    >
      {withPopularOptions && (
        <>
          <S.DrawerItemsTitle>{t('common.popular')}</S.DrawerItemsTitle>
          <S.Container>{popularOptions.map(renderOption)}</S.Container>

          <S.DrawerItemsTitle $hasTopGutter>
            {t('common.all')}
          </S.DrawerItemsTitle>
        </>
      )}

      <S.Container>
        {list.length > 0 ? (
          list.map(renderOption)
        ) : (
          <NotFound>{t('common.nothing_found')}</NotFound>
        )}
      </S.Container>
    </Drawer>
  );
};

CollectionSelectDrawer.displayName = `CollectionSelectDrawer`;
